import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { DataTable, Column } from "@/components/ui/data-table";
import { Badge, badgeVariants } from "@/components/ui/badge";
import { type VariantProps } from "class-variance-authority";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DayOffRequest, DayOffRequestStatus } from "@/types/staff-schedule";
import { useDayOffRequests } from "@/hooks/useStaffSchedule";
import { ReviewDayOffDialog } from "./ReviewDayOffDialog";

interface DayOffRequestsTableProps {
  hotelId: string;
  staffNames: Record<string, string>;
}

type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>["variant"]>;

const STATUS_BADGE_VARIANT: Record<DayOffRequestStatus, BadgeVariant> = {
  [DayOffRequestStatus.PENDING]: "new_default",
  [DayOffRequestStatus.APPROVED]: "verified",
  [DayOffRequestStatus.REJECTED]: "new_destructive",
  [DayOffRequestStatus.CANCELLED]: "new_secondary",
};

export const DayOffRequestsTable: React.FC<DayOffRequestsTableProps> = ({
  hotelId,
  staffNames,
}) => {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [reviewTarget, setReviewTarget] = useState<DayOffRequest | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const queryStatus = statusFilter === "all" ? undefined : statusFilter;
  const { data: requests, isLoading } = useDayOffRequests(hotelId, queryStatus);

  const perPage = 10;
  const totalPages = Math.max(1, Math.ceil((requests?.length ?? 0) / perPage));
  const paginatedRequests = useMemo(() => {
    if (!requests) return [];
    const start = (currentPage - 1) * perPage;
    return requests.slice(start, start + perPage);
  }, [requests, currentPage]);

  // Reset page when filter changes
  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const columns: Column<DayOffRequest>[] = useMemo(
    () => [
      {
        key: "staffId",
        header: t("staffSchedule.requests.staff"),
        render: (_, row) => (
          <span className="font-medium">{staffNames[row.staffId] ?? "—"}</span>
        ),
      },
      {
        key: "startDate",
        header: t("staffSchedule.requests.period"),
        render: (_, row) => (
          <span>
            {format(new Date(row.startDate), "MMM d")} –{" "}
            {format(new Date(row.endDate), "MMM d, yyyy")}
          </span>
        ),
      },
      {
        key: "reason",
        header: t("staffSchedule.requests.reason"),
        render: (value) => (
          <span className="text-muted-foreground">{value || "—"}</span>
        ),
      },
      {
        key: "status",
        header: t("staffSchedule.requests.status"),
        render: (value) => (
          <Badge variant={STATUS_BADGE_VARIANT[value as DayOffRequestStatus]}>
            {t(`staffSchedule.requests.${(value as string).toLowerCase()}`)}
          </Badge>
        ),
      },
      {
        key: "reviewedById",
        header: t("staffSchedule.requests.reviewedBy"),
        render: (value) => (
          <span className="text-muted-foreground">
            {value ? (staffNames[value as string] ?? "—") : "—"}
          </span>
        ),
      },
      {
        key: "actions",
        header: t("staffSchedule.requests.actions"),
        className: "text-center",
        render: (_, row) =>
          row.status === DayOffRequestStatus.PENDING ? (
            <Button
              variant="new_outline"
              size="sm"
              onClick={() => setReviewTarget(row)}
            >
              {t("staffSchedule.requests.review")}
            </Button>
          ) : null,
      },
    ],
    [t, staffNames],
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Status filter */}
      <div className="flex items-center gap-2">
        <Select value={statusFilter} onValueChange={handleStatusChange}>
          <SelectTrigger
            size="sm"
            className="w-[130px] border-slate-200 shadow-xs"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t("staffSchedule.requests.all")}
            </SelectItem>
            <SelectItem value={DayOffRequestStatus.PENDING}>
              {t("staffSchedule.requests.pending")}
            </SelectItem>
            <SelectItem value={DayOffRequestStatus.APPROVED}>
              {t("staffSchedule.requests.approved")}
            </SelectItem>
            <SelectItem value={DayOffRequestStatus.REJECTED}>
              {t("staffSchedule.requests.rejected")}
            </SelectItem>
            <SelectItem value={DayOffRequestStatus.CANCELLED}>
              {t("staffSchedule.requests.cancelled")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable<DayOffRequest>
        data={paginatedRequests}
        columns={columns}
        loading={isLoading}
        withPagination
        withSorting={false}
        pagination={{
          currentPage,
          totalPages,
          onPageChange: setCurrentPage,
        }}
        emptyMessage={t("staffSchedule.requests.empty")}
      />

      <ReviewDayOffDialog
        request={reviewTarget}
        staffName={
          reviewTarget ? (staffNames[reviewTarget.staffId] ?? "—") : ""
        }
        onClose={() => setReviewTarget(null)}
      />
    </div>
  );
};
